import { supabase } from '../lib/supabase';

// News Service - News articles and market insights management
export class NewsService {
  
  // Get all news articles with pagination
  static async getNewsArticles(options = {}) {
    try {
      const { 
        category = null, 
        limit = 20, 
        offset = 0, 
        searchQuery = null,
        featured = null,
        trending = null 
      } = options;

      let query = supabase?.from('news_articles')?.select(`
          *,
          author:user_profiles(id, full_name, avatar_url)
        `)?.order('published_at', { ascending: false });

      // Apply filters
      if (category && category !== 'all') {
        query = query?.eq('category', category);
      }

      if (featured !== null) {
        query = query?.eq('is_featured', featured);
      }

      if (trending !== null) {
        query = query?.eq('is_trending', trending);
      }

      if (searchQuery) {
        query = query?.or(`title.ilike.%${searchQuery}%, excerpt.ilike.%${searchQuery}%, content.ilike.%${searchQuery}%`);
      }

      // Apply pagination
      query = query?.range(offset, offset + limit - 1);

      const { data, error } = await query;

      if (error) {
        throw error;
      }

      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error };
    }
  }

  // Get single news article
  static async getNewsArticle(articleId) {
    try {
      const { data, error } = await supabase?.from('news_articles')?.select(`
          *,
          author:user_profiles(id, full_name, avatar_url)
        `)?.eq('id', articleId)?.single();

      if (error) {
        throw error;
      }

      // Increment view count
      await supabase?.from('news_articles')?.update({ view_count: (data?.view_count || 0) + 1 })?.eq('id', articleId);

      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }

  // Get featured articles for homepage
  static async getFeaturedArticles(limit = 5) {
    try {
      const { data, error } = await supabase?.from('news_articles')?.select(`
          id, title, excerpt, featured_image_url, category, 
          is_premium, published_at, view_count,
          author:user_profiles(full_name, avatar_url)
        `)?.eq('is_featured', true)?.order('published_at', { ascending: false })?.limit(limit);

      if (error) {
        throw error;
      }

      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error };
    }
  }

  // Get trending articles
  static async getTrendingArticles(limit = 10) {
    try {
      const { data, error } = await supabase?.from('news_articles')?.select(`
          id, title, excerpt, category, view_count,
          published_at, is_trending,
          author:user_profiles(full_name)
        `)?.eq('is_trending', true)?.order('view_count', { ascending: false })?.limit(limit);

      if (error) {
        throw error;
      }

      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error };
    }
  }

  // Get articles by category
  static async getArticlesByCategory(category, limit = 20) {
    try {
      const { data, error } = await supabase?.from('news_articles')?.select(`
          *,
          author:user_profiles(full_name, avatar_url)
        `)?.eq('category', category)?.order('published_at', { ascending: false })?.limit(limit);

      if (error) {
        throw error;
      }

      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error };
    }
  }

  // Search articles
  static async searchArticles(searchQuery, limit = 20) {
    try {
      const { data, error } = await supabase?.from('news_articles')?.select(`
          *,
          author:user_profiles(full_name, avatar_url)
        `)?.or(`title.ilike.%${searchQuery}%, excerpt.ilike.%${searchQuery}%, tags.cs.{"${searchQuery}"}`)?.order('published_at', { ascending: false })?.limit(limit);

      if (error) {
        throw error;
      }

      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error };
    }
  }

  // Get market insights
  static async getMarketInsights(collectibleType = null, limit = 10) {
    try {
      let query = supabase?.from('market_insights')?.select('*')?.order('created_at', { ascending: false })?.limit(limit);

      if (collectibleType) {
        query = query?.eq('collectible_type', collectibleType);
      }

      const { data, error } = await query;

      if (error) {
        throw error;
      }

      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error };
    }
  }

  // Get trending topics/tags
  static async getTrendingTopics(limit = 10) {
    try {
      // Since we don't have a dedicated trending topics table,
      // we'll extract from news articles tags
      const { data, error } = await supabase?.from('news_articles')?.select('tags, view_count')?.not('tags', 'is', null)?.order('view_count', { ascending: false })?.limit(50);

      if (error) {
        throw error;
      }

      // Process tags and count occurrences
      const tagCounts = {};
      data?.forEach(article => {
        article?.tags?.forEach(tag => {
          tagCounts[tag] = (tagCounts?.[tag] || 0) + (article?.view_count || 1);
        });
      });

      // Sort by count and return top topics
      const sortedTopics = Object.entries(tagCounts)?.sort(([,a], [,b]) => b - a)?.slice(0, limit)?.map(([tag, count], index) => ({
          id: index + 1,
          title: tag,
          articleCount: count,
          growthPercentage: Math.floor(Math.random() * 50) + 10 // Mock growth
        }));

      return { data: sortedTopics, error: null };
    } catch (error) {
      return { data: [], error };
    }
  }

  // Get news categories with article counts
  static async getNewsCategories() {
    try {
      const { data, error } = await supabase?.from('news_articles')?.select('category')?.not('category', 'is', null);

      if (error) {
        throw error;
      }

      // Count articles by category
      const categoryCounts = data?.reduce((acc, article) => {
        const category = article?.category;
        acc[category] = (acc?.[category] || 0) + 1;
        return acc;
      }, {}) || {};

      // Format for UI
      const categories = [
        { name: 'All', count: data?.length || 0 },
        ...Object.entries(categoryCounts)?.map(([name, count]) => ({
          name: name?.charAt(0)?.toUpperCase() + name?.slice(1)?.replace('-', ' '),
          count
        }))
      ];

      return { data: categories, error: null };
    } catch (error) {
      return { data: [], error };
    }
  }

  // Like/Unlike article
  static async toggleArticleLike(articleId, isLiked) {
    try {
      const increment = isLiked ? 1 : -1;
      
      const { data, error } = await supabase.from('news_articles').rpc('increment_like_count', {
        article_id: articleId,
        increment_value: increment
      }).select('like_count').single();

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }

  // Get article engagement stats
  static async getArticleStats(articleId) {
    try {
      const { data, error } = await supabase?.from('news_articles')?.select('view_count, like_count, comment_count')?.eq('id', articleId)?.single();

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }
}

export default NewsService;